// Change notices (ECNs), replacing the wiki's dated injection-note pages: what changed and why (markdown with
// photos), the items it affects, and when it takes effect. Releasing one (admins) releases each affected spec
// whose draft has moved on, linked to the notice; specs with no changes are left as they are.
import { Boxes, FileStack, Rocket, Search, X } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import type { ChangeNotice, ChangeNoticeItem, Item } from "../../../../shared/db.gen.ts";
import { isCurrent } from "../../../../shared/part-groups.ts"; // Part groups ---
import { nextRev, specStatusLabel } from "../../../../shared/specs.ts";
import { can, mutate, mutateWithResult } from "../../lib/mutate.ts";
import { setSearch } from "../../lib/router.tsx";
import { useRow, useRows } from "../../lib/sync.ts";
import { CreateDialog } from "../../ui/CreateDialog.tsx";
import { Badge, Button, cx, EmptyState, Input } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { DialogLayer } from "../../ui/DialogLayer.tsx";
import { ListPage } from "../../ui/ListPage.tsx";
import { type FieldDef, RecordPanel, Section } from "../../ui/RecordPanel.tsx";
import { usePaneOpen } from "../../ui/SidePane.tsx";
import { toast } from "../../ui/toast.tsx";
import { usePartGroups } from "../PartGroups.tsx"; // Part groups ---
import { day, pendingOf, type ReleasePreview, releasePreview, type SpecIndex, statusOf, useSpecIndex } from "./lib.ts";
import { MarkdownEditor } from "./MarkdownEditor.tsx";
import { FileChangeList } from "./SpecRevisions.tsx";
import { SpecStatusBadge } from "./SpecWorkspace.tsx";

const EFFECTIVE_OPTIONS = [
  { value: "next_po", label: "From the next PO" },
  { value: "date", label: "From a date" },
];
const effectiveText = (n: Pick<ChangeNotice, "effective" | "effective_date">) =>
  n.effective === "date" ? (n.effective_date ? `From ${day(n.effective_date)}` : "From a date (not set)") : "From the next PO";

type Row = ChangeNotice & { items: number; revisions: number };

export function ChangeNoticesTab({ tabs }: { tabs: ReactNode }) {
  const notices = useRows("change_notice");
  const links = useRows("change_notice_item");
  const revisions = useRows("spec_revision");
  const [creating, setCreating] = useState(false);
  const rows = useMemo<Row[]>(() => {
    const items = new Map<string, number>();
    for (const l of links) items.set(l.change_notice_id, (items.get(l.change_notice_id) ?? 0) + 1);
    const revs = new Map<string, number>();
    for (const r of revisions) if (r.change_notice_id) revs.set(r.change_notice_id, (revs.get(r.change_notice_id) ?? 0) + 1);
    return notices.map((n) => ({ ...n, items: items.get(n.id) ?? 0, revisions: revs.get(n.id) ?? 0 }));
  }, [notices, links, revisions]);

  const columns: Column<Row>[] = [
    { key: "number", header: "Number", width: "100px", cell: (r) => <span className="font-mono">{r.number || "ECN-…"}</span> },
    { key: "title", header: "Title", width: "minmax(240px,3fr)", cell: (r) => <span className="truncate font-medium">{r.title}</span> },
    {
      key: "status",
      header: "Status",
      width: "110px",
      cell: (r) => <Badge tone={r.status === "released" ? "ok" : "accent"}>{r.status === "released" ? "Released" : "Draft"}</Badge>,
    },
    {
      key: "effective",
      header: "Takes effect",
      width: "170px",
      value: (r) => (r.effective === "date" ? (r.effective_date ?? "") : "next PO"),
      cell: (r) => effectiveText(r),
    },
    { key: "items", header: "Items", width: "70px", align: "right" },
    { key: "revisions", header: "Specs released", width: "120px", align: "right", cell: (r) => (r.status === "released" ? r.revisions : "") },
    { key: "released_at", header: "Released", width: "120px", value: (r) => r.released_at ?? "", cell: (r) => day(r.released_at) },
    { key: "updated_at", header: "Updated", width: "120px", cell: (r) => day(r.updated_at) },
  ];

  return (
    <>
      <ListPage
        title="Specs"
        rows={rows}
        columns={columns}
        defaultSort={{ key: "number", desc: true }}
        newLabel="New change notice"
        onNew={can("change_notice.create") ? () => setCreating(true) : undefined}
        banner={tabs}
        pane="change_notice"
        empty={
          <EmptyState icon={<FileStack className="size-8" />} title="No change notices yet">
            A change notice records what changed and why, which items it affects and when. Releasing it releases their specs.
          </EmptyState>
        }
      />
      {creating && (
        <CreateDialog
          title="New change notice"
          fields={[{ key: "title", label: "Title", placeholder: "e.g. Trigger insert must be ferromagnetic" }]}
          onClose={() => setCreating(false)}
          onSubmit={async (v) => {
            const id = crypto.randomUUID();
            const ok = await mutate("change_notice.create", { id, title: v.title });
            if (ok) setSearch({ open: id });
            return ok;
          }}
        />
      )}
    </>
  );
}

export function ChangeNoticePanel({ id, close }: { id: string; close: () => void }) {
  const notice = useRow("change_notice", id);
  const [releasing, setReleasing] = useState(false);
  if (!notice) return null;
  const released = notice.status === "released";
  const fields: FieldDef[] = [
    { key: "title", label: "Title", readOnly: released },
    { key: "effective", label: "Takes effect", type: "select", options: EFFECTIVE_OPTIONS, readOnly: released },
    ...(notice.effective === "date" ? [{ key: "effective_date", label: "Date", type: "date" as const, readOnly: released }] : []),
  ];
  return (
    <RecordPanel
      title={notice.title}
      subtitle={notice.number}
      badges={
        <>
          <Badge tone={released ? "ok" : "accent"}>{released ? "Released" : "Draft"}</Badge>
          {released && <span className="text-[12px] text-muted">{day(notice.released_at)}</span>}
          {!released && can("change_notice.release") && (
            <Button size="sm" variant="secondary" icon={<Rocket className="size-3.5" />} onClick={() => setReleasing(true)} className="ml-auto">
              Release…
            </Button>
          )}
        </>
      }
      row={notice}
      fields={fields}
      update="change_notice.update"
      entity="change_notice"
      onClose={close}
    >
      <NoticeBody key={notice.id} notice={notice} />
      <NoticeItems notice={notice} />
      {releasing && <ReleaseNoticeDialog notice={notice} onClose={() => setReleasing(false)} />}
    </RecordPanel>
  );
}

function NoticeBody({ notice }: { notice: ChangeNotice }) {
  const editable = notice.status === "draft" && can("change_notice.update");
  const [text, setText] = useState<string | null>(null);
  const textRef = useRef(text);
  textRef.current = text;
  const save = useRef(async () => {
    const value = textRef.current;
    if (value === null) return;
    if (await mutate("change_notice.update", { id: notice.id, patch: { body_md: value } })) setText((t) => (t === value ? null : t));
  });
  useEffect(() => () => void save.current(), []);
  return (
    <Section title="What changed and why">
      <div className="px-2 pb-1">
        <MarkdownEditor
          value={text ?? notice.body_md}
          onChange={(next) => setText((prev) => (typeof next === "function" ? next(prev ?? notice.body_md) : next))}
          onSave={() => save.current()}
          readOnly={!editable}
          placeholder="What changed, why, and what the vendor must do. Paste photos."
          className="h-[300px]"
        />
      </div>
    </Section>
  );
}

function NoticeItems({ notice }: { notice: ChangeNotice }) {
  const ix = useSpecIndex();
  const links = useRows("change_notice_item");
  const items = useRows("item");
  const paneOpen = usePaneOpen();
  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const mine = links
    .filter((l) => l.change_notice_id === notice.id)
    .map((l) => ({ link: l, item: itemById.get(l.item_id) }))
    .sort((a, b) => (a.item?.sku ?? "").localeCompare(b.item?.sku ?? ""));
  const draft = notice.status === "draft";
  const editable = draft && can("change_notice_item.create");
  const releasedRevs = useMemo(
    () => new Map([...ix.revisionById.values()].filter((r) => r.change_notice_id === notice.id).map((r) => [r.spec_id, r.rev])),
    [ix, notice.id],
  );

  // The items as the usual table, its View and Columns menus by the section's title; a row opens its spec beside.
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const rows: AffectedRow[] = mine.map((m) => ({ ...m, id: m.link.id }));
  const releasedText = (item: Item | undefined) => {
    const spec = item ? ix.specByItem.get(item.id) : undefined;
    return spec && releasedRevs.has(spec.id) ? `Released Rev ${releasedRevs.get(spec.id)}` : "Not changed";
  };
  const columns: Column<AffectedRow>[] = [
    {
      key: "name",
      header: "Item",
      width: "minmax(160px,2fr)",
      value: (r) => r.item?.name ?? "Unknown item",
      cell: (r) => (
        <span className="truncate" title="Open its spec">
          {r.item?.name ?? "Unknown item"}
        </span>
      ),
    },
    {
      key: "sku",
      header: "SKU",
      width: "minmax(120px,1fr)",
      value: (r) => r.item?.sku ?? "",
      cell: (r) => <span className="truncate font-mono text-[12px] text-muted">{r.item?.sku}</span>,
    },
    {
      key: "outcome",
      header: draft ? "When released" : "Released",
      headerTitle: draft ? "What releasing the notice does to its spec" : undefined,
      width: "minmax(150px,1fr)",
      value: (r) => (draft ? outcomeText(ix, r.link.item_id) : releasedText(r.item)),
      cell: (r) => (draft ? <Outcome ix={ix} itemId={r.link.item_id} /> : <span className="truncate text-[12px] text-muted">{releasedText(r.item)}</span>),
    },
    ...(editable
      ? [
          {
            key: "remove",
            header: "",
            width: "40px",
            value: () => null,
            cell: (r) => (
              <button
                type="button"
                className="rounded p-0.5 text-faint hover:text-danger"
                title="Remove"
                onClick={(e) => {
                  e.stopPropagation();
                  mutate("change_notice_item.remove", { id: r.link.id });
                }}
              >
                <X className="size-3.5" />
              </button>
            ),
          } satisfies Column<AffectedRow>,
        ]
      : []),
  ];

  return (
    <Section title="Affected items" count={mine.length} slot={setSlot}>
      <div className="flex flex-col px-2 pb-2">
        {mine.length > 0 ? (
          <DataTable
            fit
            rows={rows}
            columns={columns}
            defaultSort={{ key: "sku" }}
            onOpen={(r) => r.item && paneOpen("spec", r.item.id)}
            rowNoun={["item", "items"]}
            exportName="Affected items"
            viewsSlot={slot}
            pageFilters={false}
          />
        ) : (
          <div className="px-2 py-1 text-muted">No items yet.</div>
        )}
        {editable && (
          <ItemSearch
            exclude={new Set(mine.map((m) => m.link.item_id))}
            onPick={async (items) => {
              for (const item of items) await mutate("change_notice_item.create", { id: crypto.randomUUID(), change_notice_id: notice.id, item_id: item.id });
            }}
          />
        )}
      </div>
    </Section>
  );
}

type AffectedRow = { id: string; link: ChangeNoticeItem; item: Item | undefined };

/** What releasing the notice would do to one item's spec, in words (Copy / CSV, sorting): as Outcome shows it. */
function outcomeText(ix: SpecIndex, itemId: string) {
  const spec = ix.specByItem.get(itemId);
  const status = statusOf(ix, spec);
  if (!spec) return specStatusLabel(status);
  const pending = pendingOf(ix, spec);
  const rev = nextRev((ix.revisionsBySpec.get(spec.id) ?? []).map((r) => r.rev));
  if (pending.any && !pending.empty) return `Releases Rev ${rev}`;
  return `${specStatusLabel(status)} · unchanged`;
}

/** What releasing the notice would do to one item's spec. */
function Outcome({ ix, itemId }: { ix: SpecIndex; itemId: string }) {
  const spec = ix.specByItem.get(itemId);
  const status = statusOf(ix, spec);
  if (!spec) return <SpecStatusBadge status={status} />;
  const pending = pendingOf(ix, spec);
  const rev = nextRev((ix.revisionsBySpec.get(spec.id) ?? []).map((r) => r.rev));
  if (pending.any && !pending.empty) return <Badge tone="warn">Releases Rev {rev}</Badge>;
  return <span className="text-[12px] text-muted">{specStatusLabel(status)} · unchanged</span>;
}

/** Finds an item to add, or every current part of a part group at once ("Add all 10 in GCU2 box sleeve"). */
function ItemSearch({ exclude, onPick }: { exclude: Set<string>; onPick: (items: Item[]) => void }) {
  const items = useRows("item");
  const { byId, membersByGroup } = usePartGroups(); // Part groups ---
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const results = useMemo(() => {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return [];
    const found = items
      .filter((i) => !i.archived && !exclude.has(i.id))
      .filter((i) => terms.every((t) => `${i.sku} ${i.name} ${i.shopify_variant_title}`.toLowerCase().includes(t)))
      .sort(
        (a, b) =>
          Number(b.sku.toLowerCase() === query.trim().toLowerCase()) - Number(a.sku.toLowerCase() === query.trim().toLowerCase()) || a.sku.localeCompare(b.sku),
      )
      .slice(0, 8);
    // Part groups --- the groups those parts are in (or named like the query), with the parts not on the notice yet.
    const groupIds = new Set(found.map((i) => i.part_group_id).filter((g): g is string => !!g));
    for (const g of byId.values()) if (terms.every((t) => g.name.toLowerCase().includes(t))) groupIds.add(g.id);
    const groups = [...groupIds]
      .map((id) => ({ group: byId.get(id), items: (membersByGroup.get(id) ?? []).filter((m) => isCurrent(m) && !exclude.has(m.id)) }))
      .filter((g) => g.group && g.items.length > 1)
      .slice(0, 2)
      .map((g) => ({ key: `g:${g.group?.id}`, label: `Add all ${g.items.length} in ${g.group?.name}`, items: g.items, sku: "" }));
    return [
      ...groups,
      ...found.map((i) => ({
        key: i.id,
        label: i.shopify_variant_title ? `${i.shopify_product_title} · ${i.shopify_variant_title}` : i.name,
        items: [i],
        sku: i.sku,
      })),
    ];
  }, [items, query, exclude, byId, membersByGroup]);
  const pick = (r: (typeof results)[number] | undefined) => {
    if (!r) return;
    onPick(r.items);
    setQuery("");
    setCursor(0);
  };
  return (
    <div className="relative mt-1">
      <Search className="pointer-events-none absolute top-2 left-2.5 size-4 text-faint" />
      <Input
        value={query}
        placeholder="Add an item… (SKU or name)"
        className="pl-8"
        onChange={(e) => {
          setQuery(e.target.value);
          setCursor(0);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") setCursor(Math.min(cursor + 1, results.length - 1));
          else if (e.key === "ArrowUp") setCursor(Math.max(cursor - 1, 0));
          else if (e.key === "Enter") pick(results[cursor]);
          else if (e.key === "Escape" && query) {
            e.stopPropagation();
            setQuery("");
          } else return;
          e.preventDefault();
        }}
      />
      {results.length > 0 && (
        <div className="absolute inset-x-0 top-full z-10 mt-1 rounded-md border border-line bg-surface p-1 shadow-pop">
          {results.map((r, n) => (
            <button
              key={r.key}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(r)}
              className={cx("flex w-full items-center gap-2 rounded px-2 py-1 text-left", n === cursor && "bg-selected")}
            >
              <span className="w-28 shrink-0 truncate font-mono text-[12px]">{r.sku || <Boxes className="size-3.5 text-faint" />}</span>
              <span className={cx("min-w-0 flex-1 truncate", !r.sku && "font-medium text-accent-text")}>{r.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

type PlanRow = { itemId: string; label: string; preview: ReleasePreview | null };

/** Each affected item and what releasing the notice would release of its spec (null: no spec), by SKU. */
function noticePlan(ix: SpecIndex, links: ChangeNoticeItem[], itemById: Map<string, Item>, noticeId: string): PlanRow[] {
  return links
    .filter((l) => l.change_notice_id === noticeId)
    .map((l) => {
      const item = itemById.get(l.item_id);
      const spec = ix.specByItem.get(l.item_id);
      return { itemId: l.item_id, label: item?.sku || item?.name || "?", preview: spec ? releasePreview(ix, spec) : null };
    })
    .sort((a, b) => a.label.localeCompare(b.label) || a.itemId.localeCompare(b.itemId));
}

const samePlan = (a: PlanRow[], b: PlanRow[]) =>
  a.length === b.length && a.every((r, i) => r.itemId === b[i].itemId && (r.preview?.stamp ?? null) === (b[i].preview?.stamp ?? null));

/**
 * Release a change notice (admins): per affected spec, exactly what would be released (text, 中文, file changes),
 * as it was when the dialog opened. The server releases only that (each spec's stamp); if anything moved on
 * meanwhile, the dialog says so and shows the new state on request.
 */
function ReleaseNoticeDialog({ notice, onClose }: { notice: ChangeNotice; onClose: () => void }) {
  const ix = useSpecIndex();
  const links = useRows("change_notice_item");
  const items = useRows("item");
  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const [plan, setPlan] = useState(() => noticePlan(ix, links, itemById, notice.id));
  const moved = !samePlan(plan, noticePlan(ix, links, itemById, notice.id));
  const [busy, setBusy] = useState(false);
  const releases = (p: PlanRow) => !!p.preview && p.preview.pending.any && !p.preview.pending.empty;
  const problems = [
    !notice.body_md.trim() && "Say what changed and why first.",
    notice.effective === "date" && !notice.effective_date && "Pick the date it takes effect.",
    !plan.length && "Add the items it affects first.",
    ...plan
      .filter(releases)
      .flatMap((p) => (p.preview?.missing ?? []).map((name) => `${p.label}: “${name}” has nothing uploaded (upload it or leave it out of the draft).`)),
  ].filter(Boolean) as string[];

  const release = async () => {
    if (busy || moved || problems.length) return;
    setBusy(true);
    const expect = Object.fromEntries(plan.map((p) => [p.itemId, p.preview?.stamp ?? null]));
    const { ok, result } = await mutateWithResult("change_notice.release", { id: notice.id, expect });
    setBusy(false);
    if (!ok) return;
    const r = result as { number: string; released: { sku: string; rev: string }[] };
    toast.ok(`Released ${r.number}${r.released.length ? `: ${r.released.map((x) => `${x.sku} Rev ${x.rev}`).join(", ")}` : ""}`);
    onClose();
  };

  return (
    <DialogLayer z={50} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          release();
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
        className="flex max-h-[80vh] w-[560px] flex-col rounded-xl border border-line bg-surface p-4 shadow-pop"
      >
        <div className="text-[15px] font-semibold">
          Release {notice.number} <span className="font-normal text-muted">{notice.title}</span>
        </div>
        <div className="mt-1 text-[12px] text-muted">
          {effectiveText(notice)}. Each affected spec whose draft has changed is released as below, linked to this notice; the rest stay as they are. All or
          nothing: if one can't be released, none are.
        </div>
        <div className="mt-3 flex min-h-0 flex-col gap-2 overflow-y-auto rounded-md bg-surface-2 px-3 py-2 text-[12px]">
          {plan.map((p) => {
            const pv = p.preview;
            return (
              <div key={p.itemId} className="flex flex-col gap-0.5">
                <div className="flex gap-2">
                  <span className="w-44 truncate font-mono">{p.label}</span>
                  {!pv ? (
                    <span className="text-muted">No spec</span>
                  ) : releases(p) ? (
                    <span className="font-medium text-warn">Releases Rev {pv.rev}</span>
                  ) : (
                    <span className="text-muted">{pv.current ? `Rev ${pv.current} · unchanged` : "Empty draft · nothing to release"}</span>
                  )}
                </div>
                {pv && releases(p) && (
                  <div className="ml-4 flex flex-col gap-0.5">
                    <div>
                      Text: {pv.pending.text ? <span className="text-warn">changed</span> : "unchanged"}
                      {" · "}中文: {pv.pending.zh ? <span className="text-warn">changed</span> : "unchanged"}
                    </div>
                    <FileChangeList changes={pv.pending.files} ix={ix} />
                    {pv.pending.files.added.length + pv.pending.files.changed.length + pv.pending.files.removed.length === 0 && (
                      <div className="text-muted">Files: unchanged</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {problems.map((p) => (
            <div key={p} className="text-danger">
              {p}
            </div>
          ))}
        </div>
        {moved && (
          <div className="mt-2 flex items-center gap-2 rounded-md bg-warn-soft px-3 py-2 text-[12px] text-warn">
            A spec or the item list changed while this was open.
            <Button size="sm" variant="secondary" className="ml-auto" onClick={() => setPlan(noticePlan(ix, links, itemById, notice.id))}>
              Show what changed
            </Button>
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" autoFocus disabled={busy || moved || problems.length > 0} kbd="↵">
            Release {notice.number}
          </Button>
        </div>
      </form>
    </DialogLayer>
  );
}
